import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Mail } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { trackConversion, trackEvent } from "@/lib/tracking";
import { supabase } from "@/integrations/supabase/client";
import { NEXT_MASTERCLASS, STRATEGY_SESSION, formatEventDate } from "@/content/events";
import { readMasterclassIntent, trackMasterclassConfirmed } from "@/lib/masterclass-analytics";
import {
  clearAbandonedCheckoutAttribution,
  readAbandonedCheckoutAttribution,
} from "@/lib/abandonedCheckoutAttribution";

export default function CheckoutReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [fired, setFired] = useState(false);
  const [confirm, setConfirm] = useState<{ status: string; price_id?: string | null; first_name?: string | null } | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    let stop = false;
    const poll = async () => {
      const { data } = await supabase.functions.invoke("checkout-status", { body: { session_id: sessionId } });
      if (stop) return;
      if (data) setConfirm(data);
      if (data?.status !== "paid" && ++tries < 8) setTimeout(poll, 2500);
    };
    void poll();
    return () => { stop = true; };
  }, [sessionId]);

  const isMasterclass = confirm?.price_id === NEXT_MASTERCLASS.priceId;
  const isSession = confirm?.price_id === STRATEGY_SESSION.priceId;
  const mc = formatEventDate(NEXT_MASTERCLASS.startsAt);

  useEffect(() => {
    if (fired) return;
    setFired(true);

    const attribution = readAbandonedCheckoutAttribution();

    // Always fire the existing conversion event.
    if (sessionId) {
      trackConversion("booking_complete", { session_id: sessionId });
      // Masterclass funnel: only fires when this visitor started from a
      // masterclass CTA in this session, so the event is a clean confirmation.
      if (readMasterclassIntent()) {
        trackMasterclassConfirmed({ session_id: sessionId });
      }
    }

    // Reached-success view: fires whenever a retry-click visitor lands on
    // the success page, even before we know the payment status. Useful for
    // funnel drop-off analysis independent of a `session_id` presence.
    if (attribution) {
      trackEvent("abandoned_checkout_success_view", {
        price_id: attribution.price_id,
        retry_stripe_session_id: attribution.stripe_session_id,
        completed_stripe_session_id: sessionId ?? undefined,
        utm_campaign: attribution.utm_campaign,
        utm_source: attribution.utm_source,
        utm_medium: "abandoned_checkout",
        seconds_since_retry_click: Math.round((Date.now() - attribution.ts) / 1000),
      });

      // Completion: only when we have a Stripe session id confirming a
      // finished checkout, so this metric mirrors actual revenue recovery.
      if (sessionId) {
        trackEvent("abandoned_checkout_completed", {
          price_id: attribution.price_id,
          retry_stripe_session_id: attribution.stripe_session_id,
          completed_stripe_session_id: sessionId,
          utm_campaign: attribution.utm_campaign,
          utm_source: attribution.utm_source,
          utm_medium: "abandoned_checkout",
          seconds_since_retry_click: Math.round((Date.now() - attribution.ts) / 1000),
        });
        // Only clear once we've captured the completion so a page refresh
        // before we saw a session_id doesn't discard attribution.
        clearAbandonedCheckoutAttribution();
      }
    }
  }, [sessionId, fired]);

  return (
    <div className="min-h-screen bg-navy-deep text-white">
      <PageMeta
        path="/checkout/return"
        noindex
        title="Payment received — Dr. Sully"
        description="Your payment was received. Confirmation and next steps are on the way."
      />
      <SiteNav />
      <main id="main" className="container max-w-2xl py-24 text-center">
        <CheckCircle2 className="h-16 w-16 text-gold mx-auto mb-6" aria-hidden="true" />
        <h1 className="font-display font-bold uppercase text-3xl md:text-5xl">
          {isMasterclass || isSession ? <>Your spot is <span className="text-gradient-gold">confirmed</span></> : <>Payment <span className="text-gradient-gold">received</span></>}
        </h1>
        {confirm?.first_name && <p className="text-silver mt-3">Thank you, {confirm.first_name}.</p>}
        {isMasterclass && (
          <div className="mt-6 border border-gold/40 p-5 text-left">
            <p className="font-display uppercase tracking-[0.2em] text-xs text-gold">Built On Purpose Masterclass</p>
            <p className="mt-2 text-white">{mc.date} · {mc.time} · {NEXT_MASTERCLASS.durationMin} minutes · {NEXT_MASTERCLASS.platform}</p>
            <p className="mt-2 text-silver text-sm">Your Zoom link and workbook will be sent to the email you used at checkout before the class. Can't make it live? You still get the full replay.</p>
          </div>
        )}
        {isSession && (
          <div className="mt-6 border border-gold/40 p-5 text-left">
            <p className="font-display uppercase tracking-[0.2em] text-xs text-gold">{STRATEGY_SESSION.durationMin}-minute strategy session</p>
            <p className="mt-2 text-silver text-sm">Dr. Sully's team will email you within one business day with session times ({STRATEGY_SESSION.platform}). If you haven't applied yet, <Link to="/strategy-session" className="underline text-white">fill out the short application</Link> so the session starts with your details.</p>
          </div>
        )}
        {confirm && confirm.status !== "paid" && <p className="mt-4 text-sm text-silver">Confirming your payment…</p>}
        <p className="text-silver mt-5 leading-relaxed">
          Thank you. A receipt has been emailed to you automatically. Dr. Sully's team will follow up within one business day with scheduling and next steps.
        </p>

        <div className="mt-8 flex items-center justify-center gap-2 text-sm text-silver/80">
          <Mail className="h-4 w-4" /> Check your inbox (and spam) for the receipt.
        </div>

        {sessionId && (
          <p className="mt-4 text-xs text-silver/50 font-mono break-all">Ref: {sessionId}</p>
        )}

        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/"
            className="inline-flex items-center justify-center border border-gold/50 text-white font-display uppercase tracking-[0.2em] text-sm px-8 py-4 hover:bg-gold/10"
          >
            Back to Home
          </Link>
          <Link
            to="/links"
            className="inline-flex items-center justify-center bg-cta text-white font-display uppercase tracking-[0.2em] text-sm px-8 py-4 shadow-cta hover:brightness-110"
          >
            Explore More
          </Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
